import { useId } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ArrowUpRight, ChevronRight, Pin } from 'lucide-react';
import { Skeleton, Tooltip } from '@/components/ui';
import {
  ConversationRowMenu,
  NEW_CONVERSATION_PATH,
  SURFACES,
  SurfaceIcon,
  conversationPath,
  isPinned,
} from '@/components/conversations';
import { useConversations, type Conversation } from '@/lib/api';
import { cn } from '@/lib/utils';
import { CONVERSATIONS_PATH } from '../navigation';
import './SideNavHistory.css';

export interface SideNavHistoryProps {
  /** Fecha a gaveta (telas estreitas) quando uma conversa é aberta. */
  onNavigate?: () => void;
  /**
   * Na gaveta do celular: linhas mais altas e o link para todas as conversas
   * sempre à vista, com texto e a seta (o "Notebooks ›" do app do Gemini) — no
   * toque não há hover que o revele.
   */
  drawer?: boolean;
}

const ALL_CONVERSATIONS = 'Ver todas as conversas';

/**
 * A seção "Conversas" da navegação expandida: as conversas recentes de quem
 * está logado, numa lista só, na ordem em que a API as devolve (as fixadas
 * primeiro, com o alfinete), cada uma com o ícone da origem (o nome vai no title
 * e na descrição) e o "⋯" de editar o nome, fixar e excluir.
 * O cabeçalho agrupa sem competir com as linhas e, no hover ou no foco, mostra o
 * link para a tela de todas as conversas (na gaveta, ele fica sempre à vista).
 * Rola sozinha; o rodapé da navegação fica fixo.
 */
export function SideNavHistory({ onNavigate, drawer = false }: SideNavHistoryProps): JSX.Element {
  const headingId = useId();
  const allLink = (
    <NavLink
      to={CONVERSATIONS_PATH}
      className="side-nav-history__all"
      // Na gaveta o texto visível ("Ver todas") abre o nome acessível (WCAG 2.5.3).
      aria-label={ALL_CONVERSATIONS}
      onClick={onNavigate}
    >
      {drawer ? (
        <>
          Ver todas
          <ChevronRight size={18} aria-hidden="true" />
        </>
      ) : (
        <ArrowUpRight size={16} aria-hidden="true" />
      )}
    </NavLink>
  );

  return (
    <section className={cn('side-nav-history', drawer && 'side-nav-history--drawer')} aria-labelledby={headingId}>
      <div className="side-nav-history__header">
        <h2 id={headingId} className="side-nav-history__heading">
          Conversas
        </h2>
        {/* Com o texto à vista, a dica só repetiria o que já está escrito. */}
        {drawer ? (
          allLink
        ) : (
          <Tooltip content={ALL_CONVERSATIONS} side="bottom">
            {allLink}
          </Tooltip>
        )}
      </div>
      <HistoryContent onNavigate={onNavigate} />
    </section>
  );
}

function HistoryContent({ onNavigate }: Pick<SideNavHistoryProps, 'onNavigate'>): JSX.Element {
  const { data, isPending, isError } = useConversations();
  const activeId = useActiveConversationId();
  const conversations = data?.items ?? [];

  if (isPending) return <HistorySkeleton />;
  if (conversations.length === 0) {
    return (
      <p className="side-nav-history__empty">
        {isError ? 'Não foi possível carregar as conversas.' : 'Sem conversas ainda'}
      </p>
    );
  }
  return (
    <ul className="side-nav-history__list" role="list">
      {conversations.map((conversation) => (
        <HistoryRow
          key={conversation.id}
          conversation={conversation}
          active={conversation.id === activeId}
          onNavigate={onNavigate}
        />
      ))}
    </ul>
  );
}

/** O id da conversa aberta no chat do Conhecimento, se houver uma. */
function useActiveConversationId(): string | null {
  const { pathname, search } = useLocation();
  if (!pathname.startsWith('/app/chat')) return null;
  return new URLSearchParams(search).get('conversa');
}

interface HistoryRowProps {
  conversation: Conversation;
  active: boolean;
  onNavigate?: () => void;
}

function HistoryRow({ conversation, active, onNavigate }: HistoryRowProps): JSX.Element {
  const navigate = useNavigate();
  const titleId = useId();
  const tagId = useId();
  const pinId = useId();
  const origin = SURFACES[conversation.surface].label;
  const pinned = isPinned(conversation);

  return (
    <li className={cn('side-nav-history__row', active && 'side-nav-history__row--active')}>
      <Link
        to={conversationPath(conversation.id)}
        className="side-nav-history__link"
        // O nome do link é o título; a origem (e a fixação) é a descrição. Somar a
        // origem ao nome faria toda linha "…Conhecimento" disputar o nome do item
        // de navegação "Conhecimento" na mesma landmark.
        aria-labelledby={titleId}
        aria-describedby={pinned ? `${tagId} ${pinId}` : tagId}
        aria-current={active ? 'page' : undefined}
        title={`${origin} · ${conversation.title}`}
        onClick={onNavigate}
      >
        <span id={tagId} className="side-nav-history__origin">
          <SurfaceIcon surface={conversation.surface} size={14} />
          <span className="sr-only">{origin}</span>
        </span>
        {pinned && (
          <span id={pinId} className="side-nav-history__pin">
            <Pin size={12} aria-hidden="true" />
            <span className="sr-only">fixada</span>
          </span>
        )}
        <span id={titleId} className="side-nav-history__title">
          {conversation.title}
        </span>
      </Link>
      <ConversationRowMenu
        conversation={conversation}
        triggerClassName="side-nav-history__more"
        // A conversa aberta acabou de sair do histórico: o chat volta ao vazio.
        onDeleted={active ? () => navigate(NEW_CONVERSATION_PATH) : undefined}
      />
    </li>
  );
}

function HistorySkeleton(): JSX.Element {
  return (
    <div className="side-nav-history__skeleton">
      {[72, 88, 64].map((width) => (
        <Skeleton key={width} width={`${width}%`} height={14} />
      ))}
    </div>
  );
}
